import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createTestimonial,
  getAllTestimonial,
  getTestimonialByID,
  TestimonialImageUpload,
  updateTestimonial,
} from "./services/testimonial.services";
import { clearTestimonialDetails } from "./testimonialSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";

import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";

const CreateTestimonial: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  // const location = useLocation();
  // const TestimonialID = location.state?.TestimonialID;
  const TestimonialID = useAppSelector((state) => state.drawer.data);
  const TestimonialDetail = useAppSelector(
    (state) => state.testimonial.TestimonialDetail
  );
  const loading = useAppSelector((state) => state.testimonial.formLoading);

  useEffect(() => {
    if (TestimonialID) {
      dispatch(getTestimonialByID(TestimonialID));
    }
    return () => {
      dispatch(clearTestimonialDetails());
    };
  }, [TestimonialID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      CustomerName: values.CustomerName ?? "",
      Description: values.Description ?? "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const TestimonialImage =
      values?.TestimonialImage && typeof values?.TestimonialImage !== "string"
        ? values.TestimonialImage[0].getRawFile()
        : "";

    try {
      if (TestimonialID) {
        const editPayload = {
          ...formData,
          ID: TestimonialID,
        };

        const response = await dispatch(updateTestimonial(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (
            values?.TestimonialImage &&
            typeof values?.TestimonialImage !== "string"
          ) {
            ImageData.append("TestimonialImage", TestimonialImage);
            ImageData.append("ID", TestimonialID);
            await dispatch(TestimonialImageUpload(ImageData));
          }
          dispatch(getAllTestimonial());
          dispatch(clearTestimonialDetails());
        }
      } else {
        const response = await dispatch(createTestimonial(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.TestimonialImage &&
            typeof values?.TestimonialImage !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("TestimonialImage", TestimonialImage);

            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(TestimonialImageUpload(ImageData));
          }
          dispatch(getAllTestimonial());
          dispatch(clearTestimonialDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <div>
        <Form
          onSubmit={handleSubmit}
          initialValues={TestimonialDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {TestimonialID ? "Update Testimonial" : "Add Testimonial"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CustomerName"
                    label="Customer Name "
                    placeholder="Customer Name "
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Description"
                    label="Description"
                    component={FormRichTextField}
                    style={{ height: 120 }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name={`TestimonialImage`}
                    label="Testimonial Image"
                    type="file"
                    accept="image/*"
                    alternateName="Testimonial Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={TestimonialID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateTestimonial;
